import type { UseQueryResult } from '@tanstack/react-query';
import Highcharts from 'highcharts/highstock';

import { getLocalTimezoneAbbreviation } from '@/components/pages/assets/capacity-test/utils';
import type { Node } from '@/data-explorer-v2/entities/node';

import { GetChartData } from '../../api/queries/get-chart-data';
import { ChartType } from '../../entities/chart-type';

import { chartTypeFormatter, chartTypeLabel, nameFromNode } from './utils';

export function isValidSeriesData(data: unknown): data is [number, number][] {
  if (!Array.isArray(data)) {
    return false;
  }

  if (data.length === 0) {
    return true;
  }

  return data.every((point) => {
    if (!Array.isArray(point) || point.length !== 2) {
      return false;
    }

    const [x, y] = point;

    if (typeof x !== 'number' || typeof y !== 'number') {
      return false;
    }

    if (!isFinite(x) || !isFinite(y)) {
      return false;
    }

    return true;
  });
}

export function optionsFromRequests(
  requests: { node: Node; query: UseQueryResult<GetChartData.Output> }[],
  chartType: ChartType,
  from: Date,
  to: Date,
): Highcharts.Options | undefined {
  const label = chartTypeLabel[chartType];
  const valueFormatter = chartTypeFormatter[chartType];

  const timezone = getLocalTimezoneAbbreviation(from);

  // Determine if we have multiple nodes to adjust naming strategy
  const hasMultipleNodes = requests.length > 1;

  const series: Highcharts.SeriesOptionsType[] = requests.flatMap(function ({
    query,
    node,
  }) {
    if (query.isLoading) {
      return [
        {
          type: 'line',
          name: nameFromNode(node, undefined, hasMultipleNodes) + ' ...',
        },
      ];
    }

    if (query.isError) {
      return [];
    }

    const chartData = query.data?.[chartType];
    if (!chartData) {
      return [];
    }

    const validSeries: Highcharts.SeriesOptionsType[] = [];

    for (const { column, data } of chartData) {
      if (!isValidSeriesData(data)) {
        console.warn(
          `Skipping invalid series data for ${chartType}/${column}:`,
          'Data is not a valid array of [number, number] tuples',
        );
        continue;
      }

      const sanitizedData = data.filter(([x, y]) => {
        const isValid = isFinite(x) && isFinite(y);
        if (!isValid) {
          console.warn(
            `Filtering out invalid data point in ${chartType}/${column}: [${x}, ${y}]`,
          );
        }
        return isValid;
      });

      if (sanitizedData.length === 0) {
        console.warn(
          `Skipping series ${chartType}/${column}: no valid data points after sanitization`,
        );
        continue;
      }

      validSeries.push({
        type: 'line',
        name: nameFromNode(node, column, hasMultipleNodes),
        data: sanitizedData,
      });
    }

    return validSeries;
  });

  // Return undefined if no valid series data exists
  if (series.length === 0) {
    return undefined;
  }

  return {
    series,
    colors: [
      '#86dec2',
      '#a6c9be',
      '#bfbfbf',
      '#c78402',
      '#9f8671',
      '#e74c3c',
      '#3498db',
      '#2ecc71',
      '#f39c12',
      '#9b59b6',
      '#1abc9c',
      '#34495e',
      '#e67e22',
      '#95a5a6',
      '#d35400',
      '#27ae60',
      '#8e44ad',
      '#2980b9',
      '#f1c40f',
      '#c0392b',
    ],
    plotOptions: {
      series: {
        borderColor: '#000000',
        pointStart: 2010,
        marker: { enabled: false },
        states: {
          hover: {
            enabled: false,
          },
        },
      },
    },
    legend: {
      enabled: true,
      align: 'right',
      verticalAlign: 'top',
      layout: 'vertical',
      maxHeight: 400,
      navigation: {
        enabled: true,
        style: {
          fontSize: '12px',
        },
      },
      itemStyle: {
        fontSize: '12px',
        fontWeight: 'normal',
      },
      itemHoverStyle: {
        color: '#333',
      },
      width: 220,
    },
    title: { text: undefined },
    time: { useUTC: false },
    xAxis: {
      type: 'datetime',
      min: from.getTime(),
      max: to.getTime(),
      crosshair: true,
      gridLineWidth: 0,
      ordinal: false,
      events: {
        afterSetExtremes: syncChartsExtremes,
      },
    },
    yAxis: {
      opposite: false,
      title: {
        text: label,
        style: { fontSize: '1rem' },
      },
      labels: {
        formatter() {
          return valueFormatter(Number(this.value));
        },
      },
    },
    tooltip: {
      pointFormatter() {
        return `${this.series.name}: <strong>${valueFormatter(Number(this.y))}</strong>`;
      },
      xDateFormat: `%Y-%m-%d %H:%M:%S (${timezone})`,
    },
    chart: {
      animation: false,
      type: 'line',
      borderWidth: 1,
      borderColor: 'none',
      height: 'auto',
      marginLeft: 120,
      marginRight: 240,
      zooming: {
        type: 'x',
        mouseWheel: false,
      },
    },
    navigator: { enabled: true },
    rangeSelector: { enabled: false },
    credits: { enabled: false },
  };
}

export function syncChartsExtremes(
  this: Highcharts.Axis,
  a: Highcharts.AxisSetExtremesEventObject,
) {
  for (const chart of Highcharts.charts) {
    if (!chart || chart === this.chart) {
      continue;
    }

    const axis = chart.xAxis.at(0);

    if (!axis) {
      continue;
    }

    const b = axis.getExtremes();

    if (a.min === b.min && a.max === b.max) {
      continue;
    }

    axis.setExtremes(a.min, a.max, true, false);
  }
}
